import {
  BytesUnitOptions,
  CustomUnitOptions,
  DecimalUnitOptions,
  PercentUnitOptions,
  TimeUnitOptions,
  UnitOptions,
} from '../schema';
import { BYTES_GROUP_CONFIG, BYTES_UNIT_CONFIG, formatBytes } from './bytes';
import { CUSTOM_GROUP_CONFIG, CUSTOM_UNIT_CONFIG, formatCustom } from './custom';
import { DECIMAL_GROUP_CONFIG, DECIMAL_UNIT_CONFIG, formatDecimal } from './decimal';
import { formatPercent, PERCENT_GROUP_CONFIG, PERCENT_UNIT_CONFIG } from './percent';
import { formatTime, TIME_GROUP_CONFIG, TIME_UNIT_CONFIG } from './time';
import { FormatValueOptions, UnitConfig, UnitGroup, UnitGroupConfig } from './types';

/**
 * Most of the number formatting is based on Intl.NumberFormat, which is built into JavaScript.
 * Prefer Intl.NumbeFormat because it covers most use cases and will continue to be supported with time.
 *
 * To format bytes, we also make use of the `numbro` package,
 * because it can handle adding units like KB, MB, GB, etc. correctly.
 */

export const UNIT_GROUP_CONFIG: Readonly<Record<UnitGroup, UnitGroupConfig>> = {
  Bytes: BYTES_GROUP_CONFIG,
  Custom: CUSTOM_GROUP_CONFIG,
  Decimal: DECIMAL_GROUP_CONFIG,
  Percent: PERCENT_GROUP_CONFIG,
  Time: TIME_GROUP_CONFIG,
};
export const UNIT_CONFIG = {
  ...BYTES_UNIT_CONFIG,
  ...CUSTOM_UNIT_CONFIG,
  ...DECIMAL_UNIT_CONFIG,
  ...PERCENT_UNIT_CONFIG,
  ...TIME_UNIT_CONFIG,
} as const;

type HasDecimalPlaces<UnitOpt> = UnitOpt extends { decimal_places?: number } ? UnitOpt : never;
type HasAbbreviate<UnitOpt> = UnitOpt extends { abbreviate?: boolean } ? UnitOpt : never;

export const UNIT_SETTINGS_NAME = 'Unit and formatting';

// TODO: Consider if we want to use scientific notation for really small numbers.
// Scientific notation can prevent really small numbers from getting too long, i.e. 0.000001 becomes 1e-6.
// Scientific notation is used by the heat map chart (see formatBucketBoundary in heat-map-model).
// Scientific notation is also hard to grok, so it might not be the right choice.
// If so, (1) which units, (2) what number is the threshold, (3) should it be tied to a configuration like `abbreviate`?

export function formatValue(value: number, options?: FormatValueOptions): string {
  if (options === undefined) {
    return value.toString();
  }

  if (isBytesUnit(options)) {
    return formatBytes(value, options);
  }

  if (isCustomUnit(options)) {
    return formatCustom(value, options);
  }

  if (isDecimalUnit(options)) {
    return formatDecimal(value, options);
  }

  if (isPercentUnit(options)) {
    return formatPercent(value, options);
  }

  if (isTimeUnit(options)) {
    return formatTime(value, options);
  }

  const exhaustive: unknown = options;
  throw new Error(`Unknown unit options ${exhaustive}`);
}

export function getUnitKindConfig(unitOptions: UnitOptions): UnitConfig {
  const unitConfig = UNIT_CONFIG[unitOptions.kind];
  if (!unitConfig) {
    // Fallback to 'Decimal' unit config for unknown unit kinds to prevent crashes
    return UNIT_CONFIG['Decimal'];
  }
  return unitConfig;
}

export function getUnitGroup(unitOptions: UnitOptions): UnitGroup {
  return getUnitKindConfig(unitOptions).group;
}

export function getUnitGroupConfig(unitOptions: UnitOptions): UnitGroupConfig {
  const unitConfig = getUnitKindConfig(unitOptions);
  return UNIT_GROUP_CONFIG[unitConfig.group];
}

// Type guards
export function isTimeUnit(unitOptions: UnitOptions): unitOptions is TimeUnitOptions {
  return getUnitGroup(unitOptions) === 'Time';
}

export function isPercentUnit(unitOptions: UnitOptions): unitOptions is PercentUnitOptions {
  return getUnitGroup(unitOptions) === 'Percent';
}

export function isDecimalUnit(unitOptions: UnitOptions): unitOptions is DecimalUnitOptions {
  return getUnitGroup(unitOptions) === 'Decimal';
}

export function isBytesUnit(unitOptions: UnitOptions): unitOptions is BytesUnitOptions {
  return getUnitGroup(unitOptions) === 'Bytes';
}

export const isCustomUnit = (unitOptions: UnitOptions): unitOptions is CustomUnitOptions => {
  return getUnitGroup(unitOptions) === 'Custom';
};

export function isUnitWithDecimalPlaces(unitOptions: UnitOptions): unitOptions is HasDecimalPlaces<UnitOptions> {
  const groupConfig = getUnitGroupConfig(unitOptions);

  return !!groupConfig.decimal_places;
}

export function isUnitWithAbbreviate(unitOptions: UnitOptions): unitOptions is HasAbbreviate<UnitOptions> {
  const groupConfig = getUnitGroupConfig(unitOptions);

  return !!groupConfig.abbreviate;
}
